import React from "react";
import { map, size } from "lodash";
import { Waypoint } from "react-waypoint";
import styled from "styled-components";
import { Spacing, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Empty from "@ui-kit/empty";
import Loading from "@ui-kit/loading";
import { tr } from "@radarly/i18n";
import { Post } from "@radarly/models/posts";
import { TellMeAicha } from "../insight-pages/aicha";

interface IProps {
  posts: Post[];
  locale: Locale;
  isLoading: boolean;
  fetchMore: () => void;
  summary?: string;
  isFetchingMore?: boolean;
  canFetchMore?: boolean;
  children: (post: Post) => React.ReactNode;
  allowEmpty?: boolean;
  withIndex?: boolean;
  withSummary?: boolean;
}

const Posts = styled.div<{ $loading: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: ${(props) => props.$loading && "hidden"};
`;

const PostContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
  &:first-child {
    margin-top: 32px;
  }
`;

const Index = styled.div`
  position: absolute;
  left: -56px;
  text-align: right;
  width: 48px;
  top: 8px;
  color: ${(props) => content(props, "secondary")};
  font-size: 18px;
`;

const FooterLoading = styled(Loading)<{ visible: boolean }>`
  margin-top: 32px;
  margin-bottom: 32px;
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  text-align: center;
`;

const StyledEmpty = styled(Empty)`
  margin-top: ${Spacing.ExtraLarge};
`;

const AichaWrapper = styled.div`
  margin-top: ${Spacing.MediumLarge};
`;

const RadarlyPosts = ({
  posts,
  summary,
  children,
  isLoading,
  isFetchingMore,
  canFetchMore,
  fetchMore,
  locale,
  allowEmpty = true,
  withIndex = true,
  withSummary,
}: IProps) => {
  const sizePosts = size(posts);
  const hasPosts = !!sizePosts;

  return (
    <Posts $loading={isLoading}>
      {isLoading ? (
        <Loading transparent />
      ) : hasPosts ? (
        <>
          {withSummary && (
            <AichaWrapper>
              <TellMeAicha
                explanation={summary}
                locale={Locale.English} // Aicha in PostList only responds in English
                show
                noCloseButton
                showCopy
              />
            </AichaWrapper>
          )}
          {map(posts, (post, index) => (
            <PostContainer key={`${post.uid}_${index}`}>
              {withIndex && <Index>{index + 1}</Index>}
              {children(post)}
            </PostContainer>
          ))}
        </>
      ) : (
        allowEmpty && <StyledEmpty message={tr(locale, "NO_RESULT")} isPage />
      )}
      <FooterLoading cover={false} inline visible={isFetchingMore} />
      {canFetchMore && (
        <Waypoint onEnter={() => fetchMore()} bottomOffset={"-300px"} />
      )}
    </Posts>
  );
};

export default RadarlyPosts;
